import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription, of, switchMap, takeUntil } from "rxjs";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { I_MyBetsFilters, I_MyBetsModel } from "../models/my-bets.models";

// Pipes
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";

// Components
import { CodereFooterComponent } from "@shared-mso-components/footer/codere-footer/codere-footer";
import { SbDropdownMenuComponent, SportsPageHeaderComponent } from "@sports-components/index";
import { MyBetsTileComponent } from "../components/my-bets-tile/my-bets-tile.component";

import { TrackingService } from "@providers/TrackingService";
import { NewBaseService } from "@providers/newBase.service";
// Services
import { DeviceService } from "@services/device.service";
// Utils
import { SportMyBetsService } from "@sports-pages/my-bets/services/sports.my-bets.service";
import { SportNavigationService } from "@sports-services/index";
import { SportTrackPollingService } from "@sports-services/index";

// Store
import { MyBetsFacade } from "@sports-pages/my-bets/stores";
import { MyBetsState } from "@sports-pages/my-bets/stores";
import * as MyBetsSelectors from "@sports-pages/my-bets/stores/my-bets.selectors";
import { SportsbookFacade } from "@sports-stores/sportsbook";

@Component({
  selector: "codere-my-bets-page",
  templateUrl: "./my-bets.page.html",
  styleUrls: ["./my-bets.page.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    SbDropdownMenuComponent,
    CodereFooterComponent,
    TranslateModule,
    MyBetsTileComponent,
    SportsPageHeaderComponent,
    SportsExtractDatePipe,
  ],
})
export class MyBetsPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;
  globalVars: GlobalVarsModel;
  translate = inject(TranslateService);
  trackingService = inject(TrackingService);
  deviceService = inject(DeviceService);
  isMobile = this.deviceService.isMobile();
  newBaseService = inject(NewBaseService);
  myBetsService = inject(SportMyBetsService);
  sportNavigationService = inject(SportNavigationService);
  sportTrackPollingService = inject(SportTrackPollingService);
  router = inject(Router);

  sportsbookFacade = inject(SportsbookFacade);

  myBetsFacade = inject(MyBetsFacade);
  myBetsStore = inject(Store<MyBetsState>);
  myBetsData$: Observable<any>;
  myBetsDataSub$: Subscription;
  myBetsTickets: I_MyBetsModel[] = [];

  emptyState = false;
  initialState = true;

  dateValue: any;
  typeValue: any;

  selectedFilterType: I_MyBetsFilters;
  selectedFilterDate: I_MyBetsFilters;

  filtersDate: I_MyBetsFilters[] = [
    { value: "2", text: this.translate.instant("histapu2d"), selected: false },
    { value: "7", text: this.translate.instant("histapu7d"), selected: true },
    { value: "15", text: this.translate.instant("histapu15d"), selected: false },
    { value: "30", text: this.translate.instant("last30days"), selected: false },
    { value: "lastmonth", text: this.translate.instant("lastmonth"), selected: false },
    { value: "90", text: this.translate.instant("histapu90d"), selected: false },
  ];

  filtersType: I_MyBetsFilters[] = [
    { value: "All", text: this.translate.instant("histaptodas"), selected: false },
    { value: "4", text: this.translate.instant("histapabiertas"), selected: true },
    { value: "1", text: this.translate.instant("histapganadas"), selected: false },
    { value: "3", text: this.translate.instant("histapperdidas"), selected: false },
    { value: "5", text: this.translate.instant("histapcobrada"), selected: false },
    { value: "0", text: this.translate.instant("histapotras"), selected: false },
  ];

  private readonly unsubscribe$ = new Subject();

  constructor() {
    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.dateValue = history.state.params ? +history.state.params.dateValue : 0;
    this.typeValue = history.state.params ? +history.state.params.typeValue : 1;

    this.selectedFilterDate = this.filtersDate[this.dateValue];
    this.selectedFilterType = this.filtersType[this.typeValue];

    this.sportTrackPollingService.stopAllPollings();

    this.myBetsData$ = this.myBetsStore.select(MyBetsSelectors.getMyBetsState);
    this.myBetsDataSub$ = this.myBetsData$.subscribe((myBetsTicketsState) => {
      this.myBetsTickets = myBetsTicketsState.myBetsTickets;
      if (myBetsTicketsState.initialStatus) return;
      this.emptyState = this.myBetsTickets.length < 1;
    });
  }

  ngOnInit() {
    this.getMyBets();
  }

  selectFilterType(filter) {
    this.emptyState = false;
    this.selectedFilterType = filter;
  }

  selectFilterDate(filter) {
    this.emptyState = false;
    this.selectedFilterDate = filter;
  }

  getCashoutByTicket(params, ticket) {
    console.log({ getCashoutByTicket: params });
    this.myBetsFacade.getCashoutByTicket({ ...params, DayOrderNr: ticket.DayOrderNr });
  }

  payOutTicket(params) {
    this.myBetsService.payOutTicket(params);
  }

  getMyBets() {
    if (!this.globalVars.user.logged) return this.globalVars.rootScope.openLogin();
    const params = {
      betType: this.selectedFilterType.value,
      ...this.getFilterDate(),
    };
    this.initialState = false;
    this.myBetsFacade.fetchMyBets(params);
  }

  getFilterDate() {
    const date = new Date();
    let endDate;
    if (this.selectedFilterDate.value === "lastmonth") {
      new Date();
      date.setDate(1);
      date.setMonth(date.getMonth() - 1);
      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    } else {
      date.setDate(date.getDate() - +this.selectedFilterDate.value);
    }
    return { date: date.toISOString(), endDate: endDate ? endDate.toISOString() : "" };
  }

  goToEvent(selection) {
    this.sportNavigationService.tryToGoToEvent(selection);
  }

  get emptyStateMessage() {
    switch (this.selectedFilterDate.value) {
      case "365":
        switch (this.selectedFilterType.value) {
          case "All":
          case "0":
            return "No hay apuestas";

          default:
            return "No hay apuestas " + this.selectedFilterType.text;
        }
      default:
        if (this.selectedFilterType.value === "0" || this.selectedFilterType.value === "All") {
          return "No hay apuestas en los " + this.selectedFilterDate.text;
        } else {
          return "No hay apuestas " + this.selectedFilterType.text + " en los " + this.selectedFilterDate.text;
        }
    }
  }

  close() {
    this.router.navigateByUrl("/");
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
